import React, { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useBookingForm } from "@/hooks/useBookingForm";
import { FormFields } from "./booking-form/FormFields";
import { usePaymentStepCta } from "./booking/usePaymentStepCta";
import { PaymentCtaButton } from "./booking/PaymentCtaButton";
import { getTranslations } from "./booking-form/constants";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { AlertCircle, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useScrollToError } from "@/hooks/useScrollToError";
import { useBookingFlow } from "@/hooks/useBookingFlow";
import { getTestModeParams } from "@/utils/urlUtils";
import { languageMap } from "@/i18n";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { useMembership } from "@/contexts/MembershipContext";
import { safeLocalStorage } from "@/utils/safeStorage";
import { supabase } from "@/integrations/supabase/client";
import {
  isMembershipEligible,
  isBloodMembershipBaseProduct,
  computeBloodMembershipPricing,
} from "@/utils/bloodMembershipPricing";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import { voucherDiscountCents } from "@/utils/voucherDiscount";
import { useMarket } from "@/hooks/useMarket";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { coerceLanguageForMarket, marketHasMembership } from "@/config/market";
import { useEmbeddedPaymentStore } from "@/stores/embeddedPaymentStore";
import { BookingLanguage } from "@/types/booking";

// This component now uses the bookingFlowStore directly
interface BookingFormProps {
  discount: {
    code: string;
    type: "percentage" | "fixed_amount";
    value: number;
  } | null;
  packageComponentMap?: Record<string, string[]>;
  onBack?: () => void;
  /** Flow marker: "membership-first" for book.aheadhealth.com, absent otherwise. */
  flow?: string;
}

const BookingForm = ({
  discount: initialDiscount,
  packageComponentMap = {},
  onBack,
  flow,
}: BookingFormProps) => {
  // Get all state from the booking flow store
  const {
    language,
    selectedTimeSlots,
    bookingType,
    selectedProducts,
    selectedAddOns,
    bookingId,
    selectedClinic,
    selectedProductDetails,
    selectedAddOnDetails,
    submitBooking,
    isLoading,
    isSubmitting,
    isSubmitted,
  } = useBookingFlow();

  const isMobile = useIsMobile();
  const { t: i18n } = useTranslation(undefined, { lng: languageMap[language] });
  const { market } = useMarket();
  const t = getTranslations(language, market);

  // Track whether the user intentionally submitted the form (vs. automatic/accidental submission)
  const userInitiatedSubmitRef = useRef(false);

  // Prevent "click teleportation" issue: when user clicks Continue on a previous step,
  // the click event can transfer to the Submit button that appears in the same position.
  // We disable form submission briefly when the component first mounts.
  const currentStep = useBookingFlowStore((state) => state.currentStep);
  const [formReady, setFormReady] = useState(false);

  useEffect(() => {
    // When form mounts on details step, disable submissions briefly
    // to prevent click teleportation from previous step's Continue button
    if (currentStep === "details") {
      setFormReady(false);
      const timer = setTimeout(() => {
        setFormReady(true);
        console.log(
          "[BookingForm] Form ready for submission after mount delay",
        );
      }, 150); // 150ms delay to let any teleported click events pass
      return () => clearTimeout(timer);
    }
  }, []); // Only run on mount

  // Membership-aware price calculation
  const { getEffectivePrice, membership, isMember } = useMembership();
  const membershipPlanActive = useMembershipPlanStore((s) => s.active);

  // Membership unified flow is Swiss-only and behind the membership_v2 flag.
  const { data: featureFlags } = useFeatureFlags();
  const membershipFlowEnabled =
    marketHasMembership(market) && (featureFlags?.["membership_v2"] ?? true);

  const offerMembership =
    membershipFlowEnabled &&
    isMembershipEligible([...selectedProductDetails, ...selectedAddOnDetails]);
  const selectedPaymentMode = useBookingFlowStore((s) => s.selectedPaymentMode);

  // Recorded deal value for a membership booking (→ total_price → Attio
  // deals.value): the per-test member basket — a single blood test at the CHF 299
  // membership base plus member-priced add-ons. NOT the annual cadence (the
  // 499/899 subscription is the membership ARR, recorded separately) and NOT the
  // non-member list prices getEffectivePrice returns without a membership token.
  // This is a RECORD, not the charge — create-blood-checkout bills the
  // subscription independently — so it intentionally DIVERGES from the displayed
  // order-summary total for Twice/Quarterly cadences (the summary shows the
  // cadence; this is the per-test deal). It coincides with the summary only on
  // Baseline. Two entry paths, mirroring the sidebar's `showMembership`:
  //   A) the dedicated cadence flow (membershipPlanStore.active), or
  //   B) "become a member & pay now" on the details step (paymentMode "yearly").
  const allSelectedDetails = [
    ...selectedProductDetails,
    ...selectedAddOnDetails,
  ];
  const showMembership =
    (membershipFlowEnabled && membershipPlanActive) ||
    (!isMember &&
      offerMembership &&
      selectedPaymentMode === "yearly" &&
      selectedProductDetails.some(isBloodMembershipBaseProduct));
  const basePrice = showMembership
    ? computeBloodMembershipPricing(allSelectedDetails).memberTotalCents
    : allSelectedDetails.reduce(
        (sum, product) => sum + getEffectivePrice(product),
        0,
      );

  // The recorded deal value (→ Attio deals.value) differs by flow:
  //  - Membership deal: the GROSS per-test member basket (flat 299 base +
  //    member-priced add-ons). The coupon does NOT reduce it — it reduces the
  //    recurring membership ARR, tracked on the membership object, not the deal.
  //    (Consolidation reconciles the deal against this gross member total.)
  //  - Non-membership deal: the voucher nets off the deal as usual.
  const totalPrice = showMembership
    ? basePrice
    : basePrice - voucherDiscountCents(initialDiscount, basePrice);

  // Get email from localStorage if it was captured in the email capture step
  const savedEmail = safeLocalStorage.getItem("userEmail") || "";

  const {
    register,
    handleSubmit: originalHandleSubmit,
    control,
    errors,
    validateBirthday,
    form,
    buildBookingPayload,
  } = useBookingForm({
    email: savedEmail,
    selectedTimeSlots,
    bookingType,
    selectedProducts,
    selectedAddOns,
    onSubmit: (data) =>
      submitBooking({
        ...data,
        discount: initialDiscount,
        total_price: totalPrice,
      }),
    bookingId: bookingId || "",
    language,
    flow,
    discount: initialDiscount,
    selectedClinic,
    packageComponentMap,
    products: selectedProductDetails,
    totalPrice, // Pass calculated totalPrice
  });

  // Member-checkout validation gate. The membership CTAs go straight to Stripe,
  // so the booking form must be valid (name, address, birthday, required
  // consents) BEFORE the embedded payment opens — otherwise the user could reach
  // payment with an incomplete request. `form.handleSubmit` runs RHF validation,
  // bumps submitCount (so the error banner + per-field errors render and
  // useScrollToError scrolls to the top), and only calls the valid callback when
  // the form passes. We resolve that outcome to the caller so the CTA can decide
  // whether to open payment. Registered in the store so the CTAs in PaymentInfo
  // (which live outside this form) can run the same gate.
  const setValidatePaymentGate = useEmbeddedPaymentStore(
    (s) => s.setValidatePaymentGate,
  );
  const validateBeforePayment = useCallback((): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      // Mark as a user-initiated submission so the validation path matches the
      // normal submit (formReady guard, error UI, scroll-to-error all apply).
      userInitiatedSubmitRef.current = true;
      void form.handleSubmit(
        () => resolve(true),
        () => resolve(false),
      )();
    });
  }, [form]);

  // Edge-independent LAST-RESORT capture: write a recovery payload straight to
  // booking_submission_attempts over PostgREST, fired by startCheckout BEFORE
  // the payment popup opens. It's the only capture that does NOT depend on an
  // edge function, so it survives create-blood-checkout / submit-4 / finalize
  // all being slow or down, AND the TWINT/Klarna/PayPal redirect tearing the
  // page down. Recovery-sufficient (not the full submit-4 payload): enough for
  // an operator to rebook by hand. The structured payload for the auto-replay
  // webhook is stashed separately (create-blood-checkout). Fire-and-forget.
  const setCaptureBookingAttempt = useEmbeddedPaymentStore(
    (s) => s.setCaptureBookingAttempt,
  );
  const captureBookingAttempt = useCallback(() => {
    void supabase
      .from("booking_submission_attempts")
      .insert({
        booking_id: bookingId || null,
        booking_payload: {
          capture: "pre-payment",
          form: form.getValues(),
          product_ids: [...selectedProducts, ...selectedAddOns],
          clinic_id: selectedClinic,
          time_slots: selectedTimeSlots,
          market,
          language,
        } as never,
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent : null,
      })
      .then(({ error }) => {
        if (error) {
          console.warn("[BookingForm] pre-payment capture FAILED:", error);
        }
      });
  }, [
    form,
    bookingId,
    selectedProducts,
    selectedAddOns,
    selectedClinic,
    selectedTimeSlots,
    market,
    language,
  ]);

  // The details-step CTA (relay submit / Stripe / pick-option) — one shared
  // decision, rendered in the content via <PaymentCtaButton> below.
  const contentPaymentCta = usePaymentStepCta(language);

  // Durable-booking link: publish a getter that assembles the FULL submit-4
  // booking payload from the current form values, so EmbeddedPaymentModal can
  // forward it to create-blood-checkout. The webhook (finalize-pending-booking)
  // replays it if the browser never returns. Builds from form.getValues() at
  // call time so the payload reflects whatever the user has entered. Cleared on
  // unmount.
  const setGetBookingPayload = useEmbeddedPaymentStore(
    (s) => s.setGetBookingPayload,
  );
  const getBookingPayload = useCallback(
    () => buildBookingPayload(form.getValues()),
    [buildBookingPayload, form],
  );

  // Publish the gate + the pre-payment capture so the out-of-form CTAs
  // (sidebar / mobile bar / PaymentInfo) run the same validation and the same
  // edge-independent capture before opening payment. Cleared on unmount.
  useEffect(() => {
    setValidatePaymentGate(validateBeforePayment);
    setCaptureBookingAttempt(captureBookingAttempt);
    setGetBookingPayload(getBookingPayload);
    return () => {
      setValidatePaymentGate(null);
      setCaptureBookingAttempt(null);
      setGetBookingPayload(null);
    };
  }, [
    validateBeforePayment,
    setValidatePaymentGate,
    captureBookingAttempt,
    setCaptureBookingAttempt,
    getBookingPayload,
    setGetBookingPayload,
  ]);

  // Wrap handleSubmit to add logging and track user-initiated submissions
  const handleSubmit = useCallback(
    (e?: React.BaseSyntheticEvent) => {
      // Block submission if form is not ready (prevents click teleportation)
      if (!formReady) {
        console.log(
          "[BookingForm] BLOCKING submission - form not ready yet (click teleportation prevention)",
        );
        e?.preventDefault();
        return;
      }

      // Mark this as a user-initiated submission
      userInitiatedSubmitRef.current = true;

      console.log("[BookingForm] handleSubmit CALLED", {
        eventType: e?.type,
        target: (e?.target as HTMLFormElement)?.id,
        targetTagName: (e?.target as HTMLElement)?.tagName,
        currentTarget: (e?.currentTarget as HTMLElement)?.id,
        submitCount: form.formState.submitCount,
        isTrusted: (e?.nativeEvent as Event)?.isTrusted,
        // Check if this is from a keyboard event
        key: (e?.nativeEvent as KeyboardEvent)?.key,
        relatedTarget: (e?.nativeEvent as FocusEvent)?.relatedTarget,
      });
      console.trace("[BookingForm] handleSubmit call stack");

      // Log the submitter if available (which button triggered the submit)
      const nativeEvent = e?.nativeEvent as SubmitEvent;
      if (nativeEvent?.submitter) {
        const submitter = nativeEvent.submitter as HTMLButtonElement;
        console.log(
          "[BookingForm] Submit triggered by button:",
          submitter.tagName,
          "id=" + submitter.id,
          "type=" + submitter.type,
          "form=" + submitter.form?.id,
          "text=" + submitter.textContent?.substring(0, 50),
        );
      } else {
        console.log(
          "[BookingForm] Submit triggered WITHOUT a submitter (possibly Enter key or programmatic)",
        );
      }

      return originalHandleSubmit(e);
    },
    [originalHandleSubmit, form.formState.submitCount, formReady],
  );

  // Log initial mount state
  useEffect(() => {
    console.log(
      "[BookingForm] MOUNTED - Initial form state:",
      JSON.stringify({
        submitCount: form.formState.submitCount,
        isSubmitted: form.formState.isSubmitted,
        errors: Object.keys(form.formState.errors),
      }),
    );
  }, []);

  // DEBUGGING: Global submit event listener to catch where submissions come from
  useEffect(() => {
    const handleGlobalSubmit = (e: Event) => {
      const target = e.target as HTMLFormElement;
      const submitter = (e as SubmitEvent)
        .submitter as HTMLButtonElement | null;
      console.log(
        "[BookingForm] GLOBAL SUBMIT EVENT CAPTURED:",
        JSON.stringify({
          formId: target?.id,
          formAction: target?.action,
          submitterTagName: submitter?.tagName,
          submitterId: submitter?.id,
          submitterType: submitter?.type,
          submitterFormAttr: submitter?.getAttribute("form"),
          submitterText: submitter?.textContent?.substring(0, 50),
        }),
      );
      console.log("[BookingForm] GLOBAL SUBMIT STACK:", new Error().stack);
    };

    document.addEventListener("submit", handleGlobalSubmit, true); // Capture phase
    return () =>
      document.removeEventListener("submit", handleGlobalSubmit, true);
  }, []);

  // Track submitCount changes
  useEffect(() => {
    console.log(
      "[BookingForm] submitCount CHANGED to:",
      form.formState.submitCount,
      "userInitiated:",
      userInitiatedSubmitRef.current,
    );
    console.log("[BookingForm] submitCount change STACK:", new Error().stack);
  }, [form.formState.submitCount]);

  // Watch for any form state changes
  useEffect(() => {
    const subscription = form.watch(() => {
      // This logs whenever any form field changes
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Log whenever isSubmitted changes
  useEffect(() => {
    console.log(
      "[BookingForm] isSubmitted CHANGED to:",
      form.formState.isSubmitted,
    );
  }, [form.formState.isSubmitted]);

  // Auto-scroll to top when validation fails
  useScrollToError(errors, form.formState.submitCount);

  const hasPrefilled = useRef(false);

  useEffect(() => {
    const formDataFromStore = form.getValues();
    const isFormEmpty =
      !formDataFromStore.firstName &&
      !formDataFromStore.lastName &&
      !formDataFromStore.email;

    if (isFormEmpty) {
      try {
        const saved = safeLocalStorage.getItem("bookingFormData");
        if (saved) {
          const savedData = JSON.parse(saved);
          console.log("[BookingForm] Restoring saved form data::", savedData);

          if (savedData.firstName)
            form.setValue("firstName", savedData.firstName);
          if (savedData.lastName) form.setValue("lastName", savedData.lastName);
          if (savedData.email) form.setValue("email", savedData.email);
          if (savedData.phone) form.setValue("phone", savedData.phone);
          if (savedData.birthday) form.setValue("birthday", savedData.birthday);
          if (savedData.genderAtBirth)
            form.setValue("genderAtBirth", savedData.genderAtBirth);
          if (savedData.street) form.setValue("street", savedData.street);
          if (savedData.streetNumber)
            form.setValue("streetNumber", savedData.streetNumber);
          if (savedData.zipCode) form.setValue("zipCode", savedData.zipCode);
          if (savedData.city) form.setValue("city", savedData.city);
          if (savedData.country) form.setValue("country", savedData.country);
          if (savedData.notes) form.setValue("notes", savedData.notes);
          // A language saved in another market is not an answer here — it
          // would leave the Select showing its placeholder (AHD-12067).
          if (savedData.preferredLanguage)
            form.setValue(
              "preferredLanguage",
              coerceLanguageForMarket(
                market,
                savedData.preferredLanguage as BookingLanguage,
              ),
            );
        }
      } catch (error) {
        console.error("[BookingForm] Error loading saved form data:", error);
      }
    }
  }, []);

  useEffect(() => {
    if (hasPrefilled.current) return;

    const { userInformation } = getTestModeParams();
    if (!userInformation) return;

    console.log("[BookingForm] Prefilling from URL params:", userInformation);
    hasPrefilled.current = true;

    if (userInformation.firstName)
      form.setValue("firstName", userInformation.firstName);
    if (userInformation.lastName)
      form.setValue("lastName", userInformation.lastName);
    if (userInformation.email) form.setValue("email", userInformation.email);
    if (userInformation.phone) form.setValue("phone", userInformation.phone);
    if (userInformation.birthday)
      form.setValue("birthday", userInformation.birthday);
    if (userInformation.genderAtBirth)
      form.setValue("genderAtBirth", userInformation.genderAtBirth);
    if (userInformation.street) form.setValue("street", userInformation.street);
    if (userInformation.streetNumber)
      form.setValue("streetNumber", userInformation.streetNumber);
    if (userInformation.zipCode)
      form.setValue("zipCode", userInformation.zipCode);
    if (userInformation.city) form.setValue("city", userInformation.city);
    if (userInformation.country)
      form.setValue("country", userInformation.country);
    if (userInformation.tosAndPpConsent) form.setValue("tosAndPpConsent", true);
    if (userInformation.medicalProcessing)
      form.setValue("medicalProcessing", true);
    if (userInformation.anonymousDataUsage)
      form.setValue("anonymousDataUsage", true);
  }, [form]);

  // Prefill form from membership data when it arrives
  const membershipPrefilled = useRef(false);
  useEffect(() => {
    if (membershipPrefilled.current) return;
    if (!membership) return;

    membershipPrefilled.current = true;
    // Membership data identifies the actual member — override stale localStorage values
    if (membership.first_name)
      form.setValue("firstName", membership.first_name);
    if (membership.last_name) form.setValue("lastName", membership.last_name);
    if (membership.email) form.setValue("email", membership.email);
    // Re-use consent captured at membership signup so the user isn't asked
    // to accept the same terms twice. The MedicalConsentField hides the two
    // required checkboxes in this case and shows a "Consented on …" notice.
    if (membership.tosAcceptedAt) form.setValue("tosAndPpConsent", true);
    if (membership.medicalConsentAcceptedAt)
      form.setValue("medicalProcessing", true);
  }, [membership, form]);

  // Debounced save of form data to localStorage as user types
  useEffect(() => {
    const subscription = form.watch((data) => {
      // Debounce saving to localStorage
      const timeoutId = setTimeout(() => {
        try {
          // Only save non-consent fields to localStorage
          const dataToSave = {
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            email: data.email || "",
            phone: data.phone || "",
            birthday: data.birthday || "",
            genderAtBirth: data.genderAtBirth || "",
            street: data.street || "",
            streetNumber: data.streetNumber || "",
            zipCode: data.zipCode || "",
            city: data.city || "",
            country: data.country || "",
            notes: data.notes || "",
            preferredLanguage: data.preferredLanguage || "",
          };
          safeLocalStorage.setItem(
            "bookingFormData",
            JSON.stringify(dataToSave),
          );
          console.log("[BookingForm] Saved form data to localStorage");
        } catch (error) {
          console.error(
            "[BookingForm] Error saving form data to localStorage:",
            error,
          );
        }
      }, 800); // Debounce for 800ms

      return () => clearTimeout(timeoutId);
    });

    return () => subscription.unsubscribe();
  }, [form]); // Re-setup subscription if form changes

  // Check if there are any validation errors AND form has been submitted at least once
  // This prevents showing errors on initial load before user tries to submit
  const hasErrors =
    Object.keys(errors).length > 0 && form.formState.submitCount > 0;

  // Check if running on localhost (development only)
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname.startsWith("127.0.0.1"));

  // Function to fill form with test data
  const fillTestData = () => {
    form.setValue("firstName", "Laurent");
    form.setValue("lastName", "Meyer");
    form.setValue("birthday", "18.05.1995");
    form.setValue("genderAtBirth", "male");
    form.setValue("phone", "+41 76 7491 47 1");
    form.setValue("email", "laurent+testphone@aheadhealth.com");
    form.setValue("street", "Heirichstr");
    form.setValue("streetNumber", "13");
    form.setValue("zipCode", "8304");
    form.setValue("city", "Wallisellen");
    form.setValue("country", "CH");
    form.setValue("tosAndPpConsent", true);
    form.setValue("medicalProcessing", true);
    form.setValue("anonymousDataUsage", true);
  };

  console.log("[BookingForm] Rendering with errors:", errors);
  console.log("[BookingForm] Form state:", form.getValues());
  console.log(
    "[BookingForm] formState.submitCount:",
    form.formState.submitCount,
  );
  console.log(
    "[BookingForm] formState.isSubmitted:",
    form.formState.isSubmitted,
  );
  console.log("BookingForm - Store state:", {
    selectedProducts,
    selectedAddOns,
    bookingType,
    selectedClinic,
    selectedProductDetails: selectedProductDetails.map((p) => ({
      id: p.id,
      name: p.name,
      type: p.product_type,
    })),
    selectedAddOnDetails: selectedAddOnDetails.map((p) => ({
      id: p.id,
      name: p.name,
      type: p.product_type,
    })),
  });

  return (
    <div className="space-y-6 ">
      <div className="text-center mb-10 lg:mb-8 px-4">
        <h1
          className="mb-2"
          style={{
            fontWeight: 400,
            color: "#2D2D2D",
            fontSize: 24,
          }}
        >
          {i18n("bookingForm.reviewTitle")}
        </h1>
        <p
          className="mx-auto leading-relaxed"
          style={{ fontSize: 14, fontWeight: 400, color: "#2D2D2D" }}
        >
          {i18n("bookingForm.reviewSubtitle")}
        </p>
      </div>

      <Form {...form}>
        {/* noValidate: defer to react-hook-form's validation (localized messages,
            error banner, scroll-to-error) instead of native browser constraint
            tooltips. Without it, native validation blocks the submit event before
            RHF runs, so its error UI never shows and the tooltip appears in the
            browser's language rather than the booking locale. */}
        <form
          onSubmit={handleSubmit}
          className="space-y-6"
          id="booking-form"
          noValidate
        >
          <div className="space-y-6">
            {/* Error banner - shows when validation fails */}
            {hasErrors && (
              <div
                data-error-banner
                className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
              >
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-600">
                    {t.formValidationError}
                  </p>
                </div>
              </div>
            )}

            {isLocalhost && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <Button
                  type="button"
                  onClick={fillTestData}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Fill with Test Data (Localhost Only)
                </Button>
              </div>
            )}

            <FormFields
              register={register}
              control={control}
              errors={form.formState.submitCount > 0 ? errors : {}}
              validateBirthday={validateBirthday}
              t={t}
              language={language}
              form={form}
              userEmail={savedEmail}
              selectedProductDetails={[
                ...selectedProductDetails,
                ...selectedAddOnDetails,
              ]}
            />

            {/* Details-step CTA — one shared decision (relay submit / Stripe /
                pick-option), the same one the sidebar and mobile bar render, so
                the in-form button never drifts from them again. Back sits beside. */}
            <div className="pt-6 pb-4">
              <div className="flex justify-between gap-2">
                {onBack && (
                  <button
                    type="button"
                    onClick={onBack}
                    disabled={isSubmitting || isLoading}
                    className="bg-neutral-50 hover:bg-neutral-100 text-aubergine-800 rounded-full h-[44px] px-4 font-medium text-base normal-case flex items-center gap-2 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {i18n("bookingForm.backButton")}
                  </button>
                )}
                <PaymentCtaButton
                  cta={contentPaymentCta}
                  className="flex-1 min-w-0 px-4"
                  submitTestId="booking-form-content-submit"
                />
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default BookingForm;
